import React, { useEffect, useLayoutEffect, useRef } from "react";
import content from "../data/content.json";
import { resolveAssetPath, sortByOrderThenId } from "../utils/content";
import "./Education.css";

const educationData = [...(content.education ?? [])].sort(sortByOrderThenId);

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));


const Education = () => {
  const sectionRef = useRef(null);
  const timelineRef = useRef(null);
  const railRef = useRef(null);
  const progressRef = useRef(null);
  const cardsRef = useRef([]);
  const headerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("edu-visible");
          }
        });
      },
      { threshold: 0.15 }
    );

    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    cardsRef.current.forEach((card) => {
      if (card) {
        observer.observe(card);
      }
    });

    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    const timeline = timelineRef.current;
    const rail = railRef.current;
    const progress = progressRef.current;

    if (!section || !timeline || !rail || !progress) {
      return undefined;
    }

    let frame = 0;

    const setRailGeometry = () => {
      const dots = timeline.querySelectorAll(".edu-dot");

      if (!dots.length) {
        return;
      }

      const timelineRect = timeline.getBoundingClientRect();
      const firstRect = dots[0].getBoundingClientRect();
      const lastRect = dots[dots.length - 1].getBoundingClientRect();

      const start = firstRect.top + firstRect.height / 2 - timelineRect.top;
      const end = lastRect.top + lastRect.height / 2 - timelineRect.top;
      const railHeight = Math.max(0, end - start);

      rail.style.top = `${start}px`;
      rail.style.height = `${railHeight}px`;
      progress.style.top = `${start}px`;
      progress.dataset.railHeight = String(railHeight);
    };

    const updateProgress = () => {
      const trackHeight = Number(progress.dataset.railHeight || 0);
      const sectionRect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      const ratio = clamp((viewportHeight - sectionRect.top) / (sectionRect.height + viewportHeight * 0.2), 0, 1);
      progress.style.height = `${trackHeight * ratio}px`;
    };

    const requestMeasure = () => {
      if (frame) {
        return;
      }

      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setRailGeometry();
        updateProgress();
      });
    };

    requestMeasure();

    window.addEventListener("scroll", requestMeasure, { passive: true });
    window.addEventListener("resize", requestMeasure);

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(requestMeasure);
      resizeObserver.observe(section);
      resizeObserver.observe(timeline);
    }

    return () => {
      window.removeEventListener("scroll", requestMeasure);
      window.removeEventListener("resize", requestMeasure);

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      if (frame) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  return (
    <section className="edu-section" id="education" ref={sectionRef}>
      <div className="edu-grid-bg" />

      <div className="edu-header" ref={headerRef}>
        <h2 className="edu-title">
          I <span className="edu-title-accent">Completed</span>
        </h2>
      </div>

      <div className="edu-timeline" ref={timelineRef}>
        <div className="edu-timeline-rail" ref={railRef} />
        <div className="edu-timeline-progress" ref={progressRef} />

        {educationData.map((edu, index) => {
          const mediaSrc = resolveAssetPath(edu.image);

          return (
            <article
              className="edu-card"
              key={edu.id ?? `${edu.degree}-${edu.year}-${index}`}
              ref={(node) => {
                cardsRef.current[index] = node;
              }}
              style={{ "--delay": `${index * 0.12}s` }}
            >
              <div className="edu-dot">
                <div className="edu-dot-ring" />
                <div className="edu-dot-inner" />
              </div>

              <div className={`edu-card-body ${mediaSrc ? "has-media" : ""}`}>
                {mediaSrc ? (
                  <div className="edu-card-media">
                    <img src={mediaSrc} alt={edu.degree || "Education"} className="edu-card-image" loading="lazy" />
                  </div>
                ) : null}

                <div className="edu-card-content">
                  <div className="edu-card-top">
                    <span className="edu-year">{edu.year}</span>
                  </div>

                  <h3 className="edu-degree">{edu.degree}</h3>
                  <p className="edu-field">{edu.field}</p>

                  <div className="edu-institution-row">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" className="edu-icon" aria-hidden="true">
                      <path
                        d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
                        stroke="#6df755"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className="edu-institution">{edu.institution}</span>
                    {edu.location ? <span className="edu-location">{edu.location}</span> : null}
                  </div>

                  <p className="edu-description">{edu.description}</p>

                  {edu.tags?.length ? (
                    <div className="edu-tags">
                      {edu.tags.map((tag) => (
                        <span key={tag} className="edu-tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default Education;
